import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { tools } from "@/lib/tools";
import { ToolCard } from "@/components/ToolCard";
import {
  ArrowRight,
  Sparkles,
  Shield,
  Zap,
  Smartphone,
  MapPin,
  Globe,
  BookOpen,
} from "lucide-react";
import criadorAsset from "@/assets/criador-mei-toolbox.webp.asset.json";
import ebookCoverAsset from "@/assets/ebook-mei-2026-cover.png.asset.json";
import { SiteSearch } from "@/components/SiteSearch";
import { trackEbookHomeClick } from "@/lib/analytics";

const ArtigosCarousel = lazy(() =>
  import("@/components/ArtigosCarousel").then((m) => ({ default: m.ArtigosCarousel })),
);
const AppDownloadSection = lazy(() =>
  import("@/components/AppDownloadSection").then((m) => ({ default: m.AppDownloadSection })),
);

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "MEI Toolbox: Calculadora DAS MEI 2026 e Ferramentas Grátis" },
      {
        name: "description",
        content:
          "Calcule DAS MEI 2026, limite de faturamento, precificação e validador de CNPJ. Mais de 25 ferramentas grátis para o MEI, sem cadastro.",
      },
      {
        property: "og:title",
        content: "MEI Toolbox — Ferramentas e artigos para Microempreendedor",
      },
      {
        property: "og:description",
        content:
          "Ferramentas grátis e guias práticos sobre MEI: DAS, formalização, Portal do Empreendedor e muito mais.",
      },
      { property: "og:url", content: "https://meitoolbox.com.br/" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      {
        name: "twitter:title",
        content: "MEI Toolbox — Ferramentas grátis para o Microempreendedor",
      },
      {
        name: "twitter:description",
        content: "Calculadora DAS, precificação, validador de CNPJ e mais. Grátis, sem cadastro.",
      },
    ],
    links: [{ rel: "canonical", href: "https://meitoolbox.com.br/" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Person",
          name: "Ismaque de Oliveira",
          jobTitle: "Criador do MEI Toolbox",
          url: "https://meitoolbox.com.br",
          worksFor: { "@type": "Organization", name: "MEI Toolbox" },
        }),
      },
    ],
  }),
  component: HomePage,
});

const faqs = [
  {
    q: "O MEI Toolbox é realmente gratuito?",
    a: "Sim. Todas as ferramentas são 100% gratuitas, sem cadastro e sem limite de uso. Não vendemos seus dados.",
  },
  {
    q: "Preciso criar uma conta?",
    a: "Não. Você usa todas as ferramentas direto no navegador. Os dados que você digita ficam salvos localmente no seu próprio dispositivo.",
  },
  {
    q: "Os cálculos do DAS são oficiais?",
    a: "Usamos os valores publicados oficialmente pelo governo para 2026. Mesmo assim, recomendamos sempre conferir no Portal do Empreendedor antes de pagar.",
  },
  {
    q: "Posso usar no celular?",
    a: "Sim. O MEI Toolbox é mobile-first e funciona perfeitamente em qualquer smartphone, tablet ou computador.",
  },
  {
    q: "Vocês substituem um contador?",
    a: "Não. Somos uma ferramenta de apoio. Para situações complexas (desenquadramento, IRPF, dúvidas tributárias), procure um contador habilitado.",
  },
  {
    q: "Como vocês ganham dinheiro se é tudo grátis?",
    a: "Atualmente não monetizamos. No futuro poderemos exibir publicidade discreta ou oferecer recursos premium opcionais — mas as ferramentas básicas continuarão gratuitas.",
  },
];

function HomePage() {
  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-hero opacity-[0.97]" />
        <div
          aria-hidden
          className="hidden md:block absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,oklch(1_0_0/0.15),transparent_60%)]"
        />
        <div className="relative container mx-auto px-4 py-14 sm:py-20 md:py-28 text-white">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 backdrop-blur px-3.5 py-1.5 text-[11px] sm:text-xs font-semibold uppercase tracking-wider">
              <Sparkles className="h-3.5 w-3.5" />
              100% grátis · sem cadastro
            </div>
            <h1 className="mt-5 sm:mt-6 font-display text-[2rem] leading-[1.1] sm:text-5xl md:text-6xl font-extrabold sm:leading-[1.05] text-balance">
              Todas as ferramentas que o MEI precisa, em um só lugar.
            </h1>
            <p className="mt-4 sm:mt-6 text-base sm:text-lg md:text-xl text-white/85 max-w-2xl text-balance">
              Calculadora DAS, limite de faturamento, precificação, validador de CNPJ e muito mais.
              Direto, gratuito e feito para o microempreendedor brasileiro.
            </p>
            <div className="mt-7 sm:mt-8 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4">
              <Link
                to="/ferramentas"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-accent px-6 py-3.5 text-base font-bold text-accent-foreground shadow-elevated hover:shadow-glow transition-all min-h-12"
              >
                Usar ferramentas grátis <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/ferramentas/$slug"
                params={{ slug: "calculadora-das-mei" }}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 bg-white/5 px-6 py-3.5 text-base font-semibold text-white hover:bg-white/10 transition min-h-12"
              >
                Calcular meu DAS
              </Link>
            </div>
            <div className="mt-6 sm:mt-8">
              <SiteSearch />
            </div>
            <div className="mt-8 sm:mt-10 grid grid-cols-3 max-w-md gap-3 sm:gap-6 text-xs sm:text-sm">
              <Stat icon={<Zap className="h-4 w-4" />} label="Sem login" />
              <Stat icon={<Shield className="h-4 w-4" />} label="Dados locais" />
              <Stat icon={<Smartphone className="h-4 w-4" />} label="Mobile-first" />
            </div>
          </div>
        </div>
      </section>

      {/* E-BOOK HIGHLIGHT */}
      <section className="container mx-auto px-4 py-10 sm:py-14 md:py-20 cv-auto">
        <div className="rounded-3xl border border-border bg-card shadow-soft overflow-hidden">
          <div className="grid md:grid-cols-2 gap-6 md:gap-10 items-center p-6 sm:p-10 md:p-12">
            <div className="flex justify-center md:justify-start">
              <div className="relative">
                <img
                  src={ebookCoverAsset.url}
                  alt="Guia Completo do MEI 2026 ebook"
                  width={320}
                  height={420}
                  loading="lazy"
                  decoding="async"
                  className="w-full max-w-[260px] sm:max-w-[280px] md:max-w-[300px] h-auto rounded-2xl shadow-md object-cover"
                />
                <span className="absolute -top-2 -right-2 sm:-top-3 sm:-right-3 inline-flex items-center rounded-full bg-cta px-2.5 py-1 text-[10px] sm:text-xs font-bold uppercase tracking-wider text-cta-foreground shadow-md">
                  Atualizado 2026
                </span>
              </div>
            </div>
            <div className="text-center md:text-left">
              <div className="text-[11px] sm:text-xs font-bold uppercase tracking-widest text-primary mb-2">
                E-book
              </div>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold leading-tight text-balance">
                Guia Completo do MEI 2026
              </h2>
              <p className="mt-3 sm:mt-4 text-muted-foreground text-[15px] sm:text-base leading-relaxed text-balance">
                Aprenda tudo sobre MEI: direitos, obrigações, impostos, benefícios e como manter seu
                CNPJ em dia com segurança.
              </p>
              <a
                href="https://meitoolbox.com.br/guia-completo-mei-2026"
                onClick={(e) => {
                  e.preventDefault();
                  console.log("[DEBUG] Ebook CTA clicked");
                  trackEbookHomeClick("Garantir meu e-book", "/guia-completo-mei-2026");
                  window.setTimeout(() => {
                    window.location.href = "https://meitoolbox.com.br/guia-completo-mei-2026";
                  }, 150);
                }}
                className="mt-6 sm:mt-7 inline-flex items-center justify-center gap-2 rounded-full bg-cta px-7 py-3.5 text-base font-bold text-cta-foreground shadow-lg hover:brightness-95 hover:-translate-y-0.5 transition-all min-h-12"
              >
                Garantir meu e-book <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* APP DOWNLOAD */}
      <Suspense fallback={<div className="container mx-auto px-4 py-12 sm:py-16 md:py-20 min-h-[250px]" />}>
        <AppDownloadSection />
      </Suspense>

      {/* TOOLS */}
      <section className="container mx-auto px-4 py-12 sm:py-16 md:py-24 cv-auto">
        <div className="flex items-end justify-between mb-8 sm:mb-10">
          <div>
            <div className="text-[11px] sm:text-xs font-bold uppercase tracking-widest text-primary mb-2">
              Ferramentas
            </div>
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold">
              Comece agora
            </h2>
          </div>
          <Link
            to="/ferramentas"
            className="hidden md:inline-flex items-center gap-1 text-sm font-semibold text-primary hover:gap-2 transition-all"
          >
            Ver todas <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid gap-4 sm:gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {tools.slice(0, 8).map((t) => (
            <ToolCard key={t.slug} tool={t} />
          ))}
        </div>
      </section>

      {/* ARTIGOS CARROSSEL */}
      <Suspense
        fallback={
          <div
            aria-hidden
            className="container mx-auto px-4 py-16 md:py-24"
            style={{ minHeight: 520 }}
          />
        }
      >
        <ArtigosCarousel />
      </Suspense>

      {/* SEO TEXT */}
      <section className="bg-surface/60 border-y border-border cv-auto">
        <div className="container mx-auto px-4 py-12 sm:py-16 md:py-20 grid md:grid-cols-3 gap-8 md:gap-10">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold leading-tight text-balance">
              Feito para quem empreende sozinho.
            </h2>
            <p className="mt-4 text-muted-foreground">
              O MEI Toolbox nasceu para resolver as dores reais de quem é microempreendedor no
              Brasil.
            </p>
          </div>
          <div className="md:col-span-2 prose prose-neutral max-w-none text-muted-foreground space-y-4 leading-relaxed text-[15px] sm:text-base">
            <p>
              Ser MEI é simples no papel, mas o dia a dia tem mil pequenas dúvidas:{" "}
              <strong className="text-foreground">quanto pagar de DAS este mês?</strong> Estou perto
              de estourar o limite de faturamento? Quanto cobrar pelo meu serviço sem trabalhar de
              graça? Esse CNPJ que o cliente me passou é válido?
            </p>
            <p>
              O MEI Toolbox reúne em um só lugar as ferramentas que você normalmente procuraria em
              cinco sites diferentes — todas gratuitas, sem login, com resultados explicados em
              linguagem clara. A{" "}
              <strong className="text-foreground">Calculadora DAS-MEI 2026</strong> mostra o valor
              exato da sua guia conforme a atividade (Comércio, Serviços ou Indústria). O{" "}
              <strong className="text-foreground">Medidor de Limite de Faturamento</strong> avisa
              quando você está se aproximando dos R$ 81.000 anuais, evitando o desenquadramento por
              excesso de receita.
            </p>
            <p>
              Para precificar serviços e produtos, a{" "}
              <strong className="text-foreground">Calculadora de Precificação</strong> considera
              seus custos fixos, variáveis e a margem de lucro desejada — para que você pare de
              chutar preço e passe a vender com previsibilidade. Já o{" "}
              <strong className="text-foreground">Validador de CNPJ</strong> confere
              instantaneamente se o número de um cliente ou fornecedor segue as regras matemáticas
              oficiais da Receita Federal.
            </p>
            <p>
              Tudo é mobile-first: você abre no celular, usa em segundos e segue a vida. Nada de
              cadastro, nada de e-mail, nada de cartão de crédito. Os dados que você digita ficam
              armazenados apenas no seu próprio dispositivo (localStorage), garantindo privacidade
              total. O MEI Toolbox não substitui um contador — para questões complexas, sempre
              procure um profissional habilitado — mas resolve as 80% das situações cotidianas que
              travam o microempreendedor.
            </p>
            <p>
              Nosso compromisso é manter as ferramentas essenciais sempre gratuitas. Estamos
              lançando novas calculadoras, geradores de documentos (recibos, contratos, notas),
              relatórios em PDF para entregar ao contador e um calendário com todas as obrigações do
              MEI em 2026. Se você é cabeleireiro, motorista de aplicativo, designer freelancer,
              dono de loja online, manicure, eletricista, confeiteira ou qualquer outro dos mais de
              14 milhões de MEIs do Brasil, este site foi feito para você.
            </p>
          </div>
        </div>
      </section>

      {/* CONHEÇA O CRIADOR — E-E-A-T */}
      <section className="container mx-auto px-4 py-12 sm:py-16 md:py-20 cv-auto">
        <article className="mx-auto max-w-3xl rounded-3xl border border-border bg-card shadow-soft p-6 sm:p-10 text-center">
          <img
            src={criadorAsset.url}
            alt="Ismaque de Oliveira, criador do MEI Toolbox"
            width={144}
            height={144}
            loading="lazy"
            decoding="async"
            className="mx-auto h-32 w-32 sm:h-36 sm:w-36 rounded-full object-cover ring-4 ring-primary/10 shadow-elevated"
          />
          <div className="mt-4 text-[11px] font-bold uppercase tracking-widest text-primary">
            Conheça o criador
          </div>
          <h2 className="mt-2 font-display text-2xl sm:text-3xl font-bold">
            Conheça o Criador do MEI Toolbox
          </h2>
          <p className="mt-1 text-base sm:text-lg font-semibold text-foreground">
            Ismaque de Oliveira
          </p>
          <div className="mt-5 space-y-3 text-[15px] sm:text-base text-muted-foreground leading-relaxed text-left sm:text-center">
            <p>
              O MEI Toolbox foi criado por{" "}
              <strong className="text-foreground">Ismaque de Oliveira</strong> com o objetivo de
              facilitar a rotina dos microempreendedores brasileiros através de ferramentas
              práticas, simuladores, calculadoras e conteúdos educativos.
            </p>
            <p>
              A plataforma reúne recursos gratuitos para ajudar o MEI a organizar finanças,
              controlar faturamento, entender obrigações fiscais e tomar decisões mais inteligentes
              para o crescimento do negócio.
            </p>
            <p>
              O compromisso do projeto é oferecer informações atualizadas, ferramentas úteis e
              soluções acessíveis para quem empreende no Brasil.
            </p>
          </div>
          <ul className="mt-6 flex flex-wrap justify-center gap-2 text-xs sm:text-sm">
            <li className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1.5 font-semibold">
              <MapPin className="h-3.5 w-3.5" /> Fortaleza — CE
            </li>
            <li className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1.5 font-semibold">
              <Globe className="h-3.5 w-3.5" /> Criador do MEI Toolbox
            </li>
            <li className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1.5 font-semibold">
              <BookOpen className="h-3.5 w-3.5" /> Ferramentas gratuitas para MEI
            </li>
          </ul>
          <Link
            to="/marca"
            className="mt-7 inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-bold text-primary-foreground shadow-soft hover:shadow-elevated transition"
          >
            Saiba mais sobre o projeto <ArrowRight className="h-4 w-4" />
          </Link>
        </article>
      </section>

      {/* FAQ */}
      <section
        id="faq"
        className="container mx-auto px-4 py-12 sm:py-16 md:py-24 max-w-3xl cv-auto"
      >
        <div className="text-center mb-8 sm:mb-12">
          <div className="text-[11px] sm:text-xs font-bold uppercase tracking-widest text-primary mb-2">
            FAQ
          </div>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold">
            Perguntas frequentes
          </h2>
        </div>
        <div className="space-y-3">
          {faqs.map((f, i) => (
            <details
              key={i}
              className="group rounded-2xl border border-border bg-card p-4 sm:p-5 shadow-soft open:shadow-elevated transition"
            >
              <summary className="cursor-pointer list-none flex items-start justify-between gap-3 font-semibold py-1">
                <span className="text-[15px] sm:text-base">{f.q}</span>
                <span className="shrink-0 flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary group-open:rotate-45 transition-transform">
                  +
                </span>
              </summary>
              <p className="mt-3 text-muted-foreground leading-relaxed text-[15px] sm:text-base">
                {f.a}
              </p>
            </details>
          ))}
        </div>
      </section>
    </>
  );
}

function Stat({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div className="flex items-center gap-2 text-white/90">
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/15">
        {icon}
      </span>
      <span className="font-semibold">{label}</span>
    </div>
  );
}
